import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEditorStore } from '../state/editorStore';
import {
  checkpointRevision,
  createTheme,
  ThemeConflictError,
  updateThemeDoc,
} from '../api/themes';
import { saveStudioDraft } from '../studioModel';
import { themeKeys } from './useThemeDoc';

const IDLE_MS = 2_000;
const MAX_WAIT_MS = 20_000;
const CHECKPOINT_EVERY = 25;

/**
 * Debounced autosave: 2s after the last edit (never more than 20s after the
 * first pending one), the working doc is written to `themes`. localStorage is
 * written on every tick as a crash cache only. Signed-out users stay local.
 * The row is created lazily on the first edit; `updated_at` rides along as an
 * optimistic-concurrency guard against another tab clobbering silently.
 */
export function useAutosave(opts: {
  userId: string | null;
  communityId?: string;
  communityName?: string;
  /** updated_at of the row the editor loaded from (concurrency baseline). */
  rowUpdatedAt: string | null;
  onRowCreated?: (id: string) => void;
}) {
  const qc = useQueryClient();
  const editSeq = useEditorStore((s) => s.editSeq);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstPendingAt = useRef<number | null>(null);
  const updatedAtRef = useRef<string | null>(opts.rowUpdatedAt);
  const creating = useRef(false);
  const savesSinceCheckpoint = useRef(0);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // A fresh load (new row baseline) resets the concurrency guard.
  useEffect(() => {
    updatedAtRef.current = opts.rowUpdatedAt;
  }, [opts.rowUpdatedAt]);

  useEffect(() => {
    if (editSeq === 0) return; // load, not an edit
    const store = useEditorStore.getState();

    // Crash cache — every edit, unconditionally.
    saveStudioDraft(store.flat, optsRef.current.communityId);

    if (!optsRef.current.userId) return; // guest: local only

    if (firstPendingAt.current == null) firstPendingAt.current = Date.now();
    const elapsed = Date.now() - firstPendingAt.current;
    const wait = Math.max(250, Math.min(IDLE_MS, MAX_WAIT_MS - elapsed));

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void save();
    }, wait);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSeq]);

  async function save(): Promise<void> {
    const s = useEditorStore.getState();
    const { userId, communityId, communityName, onRowCreated } = optsRef.current;
    if (!userId || !s.dirty || s.saving) return;

    const seqAtStart = s.editSeq;
    s.markSaving();
    firstPendingAt.current = null;

    try {
      if (!s.themeRowId) {
        if (creating.current) return;
        creating.current = true;
        const row = await createTheme({
          ownerId: userId,
          doc: s.doc(),
          name: communityId ? `${communityName ?? s.flat.communityName} theme` : s.themeName,
          communityId,
        });
        creating.current = false;
        useEditorStore.getState().setThemeRow(row.id, row.name);
        updatedAtRef.current = row.updated_at;
        onRowCreated?.(row.id);
      } else {
        const row = await updateThemeDoc(s.themeRowId, s.doc(), updatedAtRef.current ?? '');
        updatedAtRef.current = row.updated_at;
        savesSinceCheckpoint.current += 1;
        if (savesSinceCheckpoint.current >= CHECKPOINT_EVERY) {
          savesSinceCheckpoint.current = 0;
          void checkpointRevision(row.id, s.doc(), userId).then(() =>
            qc.invalidateQueries({ queryKey: themeKeys.revisions(row.id) }),
          );
        }
      }
      useEditorStore.getState().markSaved();
      void qc.invalidateQueries({ queryKey: themeKeys.mine });

      // Edits landed while the save was in flight — they're still unsaved.
      const after = useEditorStore.getState();
      if (after.editSeq !== seqAtStart) {
        useEditorStore.setState({ dirty: true });
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => void save(), IDLE_MS);
      }
    } catch (e) {
      creating.current = false;
      const msg =
        e instanceof ThemeConflictError
          ? 'Updated in another tab — reload to sync'
          : (e as Error).message || 'Could not save';
      useEditorStore.getState().markSaveError(msg);
    }
  }

  /** Force an immediate flush (Ctrl+S, and before publish). */
  async function flush(): Promise<void> {
    if (timer.current) clearTimeout(timer.current);
    await save();
  }

  return { flush };
}
